"use client";

import { useEffect, useState } from "react";
import { fetchPricingPlans } from "@/lib/api/pricing-plans";

export type StartingPrice = {
  /** e.g. "₹299/month" */
  full: string;
  /** e.g. "₹299/mo" */
  short: string;
  /** e.g. "₹299" — bare amount, for large price displays that render the suffix separately. */
  amount: string;
  /** e.g. "/month" */
  suffix: string;
  /** e.g. "₹499" — only set when the cheapest plan is discounted. */
  originalPrice?: string;
  /** e.g. 40 — only set when the cheapest plan is discounted. */
  discountPercent?: number;
};

/** Cheapest live plan's price, formatted for CTA copy; undefined until the API responds. */
export function useStartingPrice() {
  const [price, setPrice] = useState<StartingPrice>();

  useEffect(() => {
    fetchPricingPlans().then((plans) => {
      const [cheapest] = plans;

      if (!cheapest) {
        return;
      }

      const full = `${cheapest.price}${cheapest.priceSuffix}`;
      const toNumber = (value: string) => Number(value.replace(/[^\d.]/g, ""));
      const original = cheapest.originalPrice
        ? toNumber(cheapest.originalPrice)
        : 0;
      const current = toNumber(cheapest.price);

      setPrice({
        full,
        short: full.replace(/\/month$/, "/mo"),
        amount: cheapest.price,
        suffix: cheapest.priceSuffix,
        originalPrice: cheapest.originalPrice,
        discountPercent:
          original > current
            ? Math.round(((original - current) / original) * 100)
            : undefined,
      });
    });
  }, []);

  return price;
}
